import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';

const API = process.env.REACT_APP_BACKEND_URL;

const difficultyColor = (c) => ({ Easy: c.success, Medium: c.warning, Hard: c.error });
const difficultyBg    = (c) => ({
  Easy:   c.theme === 'light' ? '#d1fae5' : '#051a10',
  Medium: c.theme === 'light' ? '#fef3c7' : '#1a1000',
  Hard:   c.theme === 'light' ? '#fee2e2' : '#1a0808',
});

const topicIcons = {
  'Apex Fundamentals':      '📘',
  'Data Querying':          '🔍',
  'Data Manipulation':      '💾',
  'Object-Oriented Apex':   '🏗️',
  'Apex Triggers':          '⚡',
  'Asynchronous Apex':      '⏳',
  'Apex Testing':           '🧪',
  'Error Handling':         '🚨',
  'Custom Metadata':        '📋',
  'Working with JSON':      '🔄',
  'REST Callouts':          '🌐',
  'Mocks & Testing':        '🧪',
  'Platform Events':        '📡',
  'LWC Fundamentals':       '🖥️',
  'LWC Advanced':           '⚡',
  'LWC - Apex Integration': '🔗',
  'Bug Hunt':               '🐛',
};

const topicOrder = [
  'Apex Fundamentals','Data Querying','Data Manipulation',
  'Object-Oriented Apex','Apex Triggers','Asynchronous Apex',
  'Apex Testing','Error Handling','Custom Metadata','Working with JSON',
  'REST Callouts','Mocks & Testing','Platform Events',
  'LWC Fundamentals','LWC Advanced','LWC - Apex Integration',
  'Bug Hunt',
];

const categories = [
  { id: 'apex',         label: 'Apex'         },
  { id: 'soql',         label: 'SOQL'         },
  { id: 'lwc',          label: 'LWC'          },
  { id: 'integrations', label: 'Integrations' },
];

export default function Challenges() {
  const { category }      = useParams();
  const navigate          = useNavigate();
  const { user }          = useAuth();
  const { theme }         = useTheme();
  const c                 = { ...TOKENS[theme], theme };

  // Apex30 challenges live on /apex30, not here
  useEffect(() => {
    if (category === 'apex30') navigate('/apex30', { replace: true });
  }, [category, navigate]);
  const [challenges, setChallenges]         = useState([]);
  const [solvedIds, setSolvedIds]           = useState(new Set());
  const [expandedTopic, setExpandedTopic]   = useState(null);
  const [loading, setLoading]               = useState(true);

  useEffect(() => {
    setLoading(true);
    // For apex category, also fetch 'broken' challenges and merge them under 'Bug Hunt' topic
    const fetchUrl = (category === 'apex' || !category)
      ? `${API}/challenges`
      : `${API}/challenges`;

    fetch(fetchUrl)
      .then(r => r.json())
      .then(data => {
        let filtered;
        if (!category || category === 'apex') {
          // Include both apex and broken categories
          filtered = data.filter(ch => ch.category === 'apex' || ch.category === 'broken');
          // Tag broken challenges with Bug Hunt topic
          filtered = filtered.map(ch =>
            ch.category === 'broken' ? { ...ch, topic: 'Bug Hunt' } : ch
          );
        } else {
          filtered = data.filter(ch => ch.category === category);
        }
        setChallenges(filtered);
        // Auto-expand first topic
        const topics = [...new Set(filtered.map(ch => ch.topic))];
        const orderedFirst = topicOrder.find(t => topics.includes(t));
        if (orderedFirst) setExpandedTopic(orderedFirst);
        setLoading(false);
      });
  }, [category]);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/submissions/${user.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.solvedIds) setSolvedIds(new Set(data.solvedIds.map(id => String(id))));
      });
  }, [user]);

  const handleLearnClick = (e, topic) => {
    e.stopPropagation();
    navigate('/learn/' + encodeURIComponent(topic));
  };

  const isSolved = (id) => solvedIds.has(String(id));

  const grouped = challenges.reduce((acc, ch) => {
    if (!acc[ch.topic]) acc[ch.topic] = [];
    acc[ch.topic].push(ch);
    return acc;
  }, {});
  Object.keys(grouped).forEach(t => {
    grouped[t].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  });
  const orderedTopics = topicOrder.filter(t => grouped[t]);

  const dColor = difficultyColor(c);
  const dBg    = difficultyBg(c);

  // Skeleton loading
  if (loading) return (
    <div style={{ background: c.bg, minHeight: '100vh', transition: 'background 0.25s' }}>
      <Navbar />
      <div style={{ padding: '28px 40px 0', maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ height: 28, width: 200, background: c.card, borderRadius: 6, marginBottom: 12 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginTop: 24 }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, height: 120 }} />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif", transition: 'background 0.25s, color 0.25s' }}>
      <SEO
        title={`${(category || 'apex').toUpperCase()} Challenges`}
        description={`Browse Salesforce ${(category || 'apex').toUpperCase()} coding challenges — practice Apex, SOQL, and LWC with real code problems.`}
        url={`https://trailcode.org/challenges/${category || 'apex'}`}
      />

      <Navbar />

      {/* Category tabs */}
      <div style={{ borderBottom: `1px solid ${c.border}`, background: c.navBg }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '0 40px', display: 'flex', gap: 6 }}>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => navigate(`/challenges/${cat.id}`)} style={{
              background: 'transparent',
              color: (category || 'apex') === cat.id ? c.accent : c.secondary,
              border: 'none',
              borderBottom: `2px solid ${(category || 'apex') === cat.id ? c.accent : 'transparent'}`,
              padding: '12px 16px', cursor: 'pointer',
              fontSize: '13px', fontWeight: (category || 'apex') === cat.id ? 600 : 400,
              transition: 'all 0.15s',
            }}>{cat.label}</button>
          ))}
        </div>
      </div>

      {/* Header */}
      <div style={{ padding: '28px 40px 0', maxWidth: '960px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 6px', letterSpacing: '-0.3px', color: c.text }}>
          {(category || 'apex').toUpperCase()} Challenges
        </h1>
        <p style={{ color: c.secondary, fontSize: '13px', margin: '0 0 24px' }}>
          {challenges.length} challenges
          {solvedIds.size > 0 && <span style={{ color: c.accent, marginLeft: '8px', fontWeight: 500 }}>· {solvedIds.size} solved ☁️</span>}
        </p>
      </div>

      {/* Empty state */}
      {orderedTopics.length === 0 && (
        <div style={{ padding: '60px 40px', maxWidth: '960px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: 32, marginBottom: 12 }}>🚧</p>
          <p style={{ fontSize: 16, fontWeight: 600, color: c.text, marginBottom: 8 }}>Coming soon</p>
          <p style={{ fontSize: 13, color: c.secondary }}>Challenges for this category are being added. Check back soon!</p>
        </div>
      )}

      {/* Topic tiles grid + inline expanded challenges */}
      <div style={{ padding: '0 40px 60px', maxWidth: '960px', margin: '0 auto' }}>

        {/* Tiles grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 0 }}>
          {orderedTopics.map(topic => {
            const topicChallenges = grouped[topic] || [];
            const solvedCount     = topicChallenges.filter(ch => isSolved(ch.id)).length;
            const total           = topicChallenges.length;
            const progressPct     = total > 0 ? (solvedCount / total) * 100 : 0;
            const isExpanded      = expandedTopic === topic;
            const isBugHunt       = topic === 'Bug Hunt';

            return (
              <div
                key={topic}
                onClick={() => setExpandedTopic(isExpanded ? null : topic)}
                style={{
                  background: isExpanded
                    ? (isBugHunt ? 'rgba(239,68,68,0.08)' : c.accentBg)
                    : c.card,
                  border: `1.5px solid ${isExpanded
                    ? (isBugHunt ? '#ef4444' : c.accent)
                    : c.border}`,
                  borderRadius: 14,
                  padding: '18px 16px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
                onMouseEnter={e => {
                  if (!isExpanded) {
                    e.currentTarget.style.borderColor = isBugHunt ? '#ef4444' : c.accent;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={e => {
                  if (!isExpanded) {
                    e.currentTarget.style.borderColor = c.border;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {/* Icon + title row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 24 }}>{topicIcons[topic] || '📝'}</span>
                  {isBugHunt && (
                    <span style={{ fontSize: 9, fontWeight: 700, background: '#ef444422', color: '#ef4444', border: '1px solid #ef444444', padding: '2px 6px', borderRadius: 99 }}>BUG HUNT</span>
                  )}
                  {isExpanded && !isBugHunt && (
                    <span style={{ fontSize: 9, fontWeight: 700, background: c.accentBg, color: c.accent, border: `1px solid ${c.accentBorder}`, padding: '2px 6px', borderRadius: 99 }}>OPEN</span>
                  )}
                </div>

                <div style={{ fontSize: 14, fontWeight: 600, color: c.text, lineHeight: 1.3 }}>{topic}</div>

                {/* Progress */}
                <div style={{ fontSize: 11, color: c.muted }}>{solvedCount}/{total} completed</div>
                <div style={{ height: 3, background: c.border, borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${progressPct}%`, background: isBugHunt ? '#ef4444' : c.accent, borderRadius: 99, transition: 'width 0.3s' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Inline expanded challenges — renders below the full grid */}
        {expandedTopic && grouped[expandedTopic] && (
          <div style={{
            marginTop: 16,
            background: c.card,
            border: `1.5px solid ${expandedTopic === 'Bug Hunt' ? '#ef4444' : c.accent}`,
            borderRadius: 14,
            overflow: 'hidden',
            animation: 'fadeIn 0.2s ease',
          }}>
            {/* Topic header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${c.border}`, background: expandedTopic === 'Bug Hunt' ? 'rgba(239,68,68,0.06)' : c.accentBg }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{topicIcons[expandedTopic] || '📝'}</span>
                <div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: c.text }}>{expandedTopic}</span>
                  {expandedTopic === 'Bug Hunt' && (
                    <p style={{ fontSize: 11, color: '#ef4444', margin: '2px 0 0' }}>Find and fix the bugs — no hints!</p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {expandedTopic !== 'Bug Hunt' && (
                  <button
                    onClick={e => handleLearnClick(e, expandedTopic)}
                    style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '4px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = c.accent; e.currentTarget.style.color = c.accent; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.color = c.secondary; }}>
                    📖 Learn
                  </button>
                )}
                <button
                  onClick={() => setExpandedTopic(null)}
                  style={{ background: 'transparent', border: 'none', color: c.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
              </div>
            </div>

            {/* Challenge rows */}
            {(grouped[expandedTopic] || []).map((challenge, idx) => {
              const solved   = isSolved(challenge.id);
              const solvedBg = theme === 'light' ? '#f0f7ff' : '#0a0f1a';
              const isBug    = expandedTopic === 'Bug Hunt';
              return (
                <div
                  key={challenge.id}
                  onClick={() => navigate(`/challenge/${challenge.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '13px 20px',
                    borderBottom: idx < (grouped[expandedTopic] || []).length - 1 ? `1px solid ${c.border}` : 'none',
                    cursor: 'pointer',
                    background: solved ? solvedBg : c.card,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = c.cardHover}
                  onMouseLeave={e => e.currentTarget.style.background = solved ? solvedBg : c.card}
                >
                  {/* Number/check circle */}
                  <div style={{
                    width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: solved ? c.accentBg : (isBug ? 'rgba(239,68,68,0.1)' : c.bg),
                    border: `1.5px solid ${solved ? c.accent : (isBug ? '#ef444466' : c.border)}`,
                    fontSize: '12px', fontWeight: 600,
                    color: solved ? c.accent : (isBug ? '#ef4444' : c.secondary),
                  }}>
                    {solved ? '✓' : (isBug ? '🐛' : (challenge.order_index || idx + 1))}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: c.text }}>{challenge.title}</span>
                      {solved && (
                        <span style={{ fontSize: '11px', color: c.accent, background: c.accentBg, border: `1px solid ${c.accentBorder}`, padding: '1px 8px', borderRadius: '20px', fontWeight: 500 }}>☁️ solved · retry?</span>
                      )}
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: dColor[challenge.difficulty], background: dBg[challenge.difficulty], padding: '1px 8px', borderRadius: '10px' }}>{challenge.difficulty}</span>
                  </div>

                  <span style={{ color: solved ? c.accent : (isBug ? '#ef4444' : c.muted), fontSize: '14px' }}>→</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}